/**
 * Lazy "Complete the Set" for the cart drawer.
 *
 * The recommendations (family cross-sells + curated add-ons) are rendered by the
 * `cart-recommendations` section into an inert <template>, NOT inside `cart-drawer`, so
 * they never re-render on the add-to-cart critical path. This script copies that markup
 * into the drawer's [data-cart-recos-slot]:
 *
 *   - at page load: fill the slot from the template (so it's present when the drawer is
 *     opened with items already in the cart — even after navigating to another page).
 *   - after cart:change (add / qty change): the drawer re-renders (slot emptied) and the
 *     cart's contents changed, so refetch the section (?section_id=cart-recommendations),
 *     refresh the template, and refill.
 *
 * Using a <template> keeps the source markup inert: its custom elements (scroll-carousel,
 * carousel buttons) don't upgrade and its ids aren't live, so there's exactly ONE live
 * copy (in the slot) and the carousel's aria-controls resolves to the visible carousel.
 */
(function () {
  var SECTION = 'cart-recommendations';
  var fetching = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function slot() {
    return document.querySelector('#cart-drawer [data-cart-recos-slot]');
  }
  function template() {
    return document.querySelector('template[data-cart-recos-template]');
  }

  // Copy the template markup into the drawer slot (only live instance of this markup).
  function fill() {
    var s = slot();
    var t = template();
    if (!s || !t) return;
    s.innerHTML = t.innerHTML; // upgrades custom elements in the slot; template stays inert
  }

  // Shimmer placeholders shown while the fresh recommendations are fetched after a cart change.
  function skeletonHTML(cards) {
    var card =
      '<div class="jn-recos-skel__card">' +
        '<div class="jn-recos-skel__img"></div>' +
        '<div class="jn-recos-skel__info">' +
          '<span class="jn-recos-skel__line"></span>' +
          '<span class="jn-recos-skel__line jn-recos-skel__line--sm"></span>' +
          '<span class="jn-recos-skel__line jn-recos-skel__line--btn"></span>' +
        '</div>' +
      '</div>';
    var row = '';
    for (var i = 0; i < cards; i++) row += card;
    return '<div class="jn-recos-skel" aria-hidden="true">' +
             '<span class="jn-recos-skel__head"></span>' +
             '<div class="jn-recos-skel__row">' + row + '</div>' +
           '</div>';
  }
  function showSkeleton() {
    var s = slot();
    if (s) s.innerHTML = skeletonHTML(3);
  }

  // Refetch the section for the current cart, refresh the template, then refill the slot.
  async function refresh() {
    if (fetching) return;
    fetching = true;
    try {
      var res = await fetch(root() + '?section_id=' + SECTION, { credentials: 'same-origin' });
      if (res.ok) {
        var text = await res.text();
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('template[data-cart-recos-template]');
        var t = template();
        if (fresh && t) t.innerHTML = fresh.innerHTML; // keep the source current for the next open
      }
    } catch (e) {
      /* recommendations are non-critical — fall back to the current template below */
    } finally {
      fetching = false;
      fill(); // always replace the skeleton (with fresh recs, or the current template on failure)
    }
  }

  function init() {
    fill(); // present before the drawer is ever opened
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cart changed (add / update): the theme replaces the drawer body (slot emptied) and the
  // cart contents differ, so refetch. Deferred a frame so it runs after the theme's own
  // cart:change handler has swapped in the new drawer content.
  document.addEventListener('cart:change', function () {
    requestAnimationFrame(function () {
      showSkeleton(); // instant shimmer placeholders...
      refresh();      // ...replaced when the fresh recommendations arrive
    });
  });
})();
